import { useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Server,
  RefreshCw,
  XCircle,
  CheckCircle,
  Settings,
  Plug,
  ArrowUpCircle,
  Loader2,
} from 'lucide-react'
import { type Site } from './shared'
import {
  useSiteRemoteStatus,
  useSiteRemoteSettings,
  useSitePlugins,
  useUpdateRemoteSettings,
  useUpdateRemotePlugin,
  useUpdateVersionCache,
  useCachePlugins,
} from '../../hooks/useSites'

interface ManageTabProps {
  site: Site
}

export function ManageTab({ site }: ManageTabProps) {
  const { data: status, isLoading: statusLoading, error: statusError, refetch: refetchStatus } = useSiteRemoteStatus(site.domain)
  const { data: remoteSettings, isLoading: settingsLoading, refetch: refetchSettings } = useSiteRemoteSettings(site.domain)
  const { data: pluginsData, isLoading: pluginsLoading, refetch: refetchPlugins } = useSitePlugins(site.domain)
  const updateSettings = useUpdateRemoteSettings()
  const updatePlugin = useUpdateRemotePlugin()
  const updateVersionCache = useUpdateVersionCache()
  const cachePlugins = useCachePlugins()

  const isLoading = statusLoading || settingsLoading || pluginsLoading
  const isConnected = status?.success && status?.status === 'healthy'

  // Cache versions when status is fetched successfully
  useEffect(() => {
    if (status?.success && status.wp_version && status.php_version && status.version) {
      updateVersionCache.mutate({
        domain: site.domain,
        wp_version: status.wp_version,
        php_version: status.php_version,
        jezpress_version: status.version,
      })
    }
  }, [status?.success, status?.wp_version, status?.php_version, status?.version, site.domain])

  // Cache plugins when plugins are fetched successfully
  useEffect(() => {
    if (pluginsData?.success && pluginsData.plugins) {
      cachePlugins.mutate({
        domain: site.domain,
        plugins: pluginsData.plugins.map(p => ({
          slug: p.slug,
          name: p.name,
          version: p.version,
          active: p.active,
          update_available: p.update_available,
          new_version: p.new_version || undefined,
        })),
      })
    }
  }, [pluginsData?.success, pluginsData?.plugins, site.domain])

  // Handle OAuth toggle
  const handleOAuthToggle = async (checked: boolean) => {
    await updateSettings.mutateAsync({
      domain: site.domain,
      settings: { disable_password_login: checked },
    })
  }

  // Handle plugin update
  const handleUpdatePlugin = async (slug: string) => {
    await updatePlugin.mutateAsync({ domain: site.domain, slug })
  }

  // Refresh all data
  const handleRefresh = () => {
    refetchStatus()
    refetchSettings()
    refetchPlugins()
  }

  // Not connected / error state
  if (statusError || (status && !status.success)) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Remote Management</h4>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" />
            <span className="font-medium">Cannot Connect</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Unable to connect to the site's REST API. Make sure:
          </p>
          <ul className="mt-2 text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>JezPress plugin v2.4.0+ is installed</li>
            <li>The site is accessible</li>
            <li>The plugin is activated</li>
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Connection Status</span>
        </div>
        <div className="flex items-center gap-2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isConnected ? (
            <Badge variant="outline" className="border-green-500 text-green-600 dark:text-green-400">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          ) : (
            <Badge variant="destructive">
              <XCircle className="h-3 w-3 mr-1" />
              Disconnected
            </Badge>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleRefresh}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Version Info */}
      {status && (
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div className="p-2 rounded bg-muted/50">
            <div className="text-muted-foreground text-xs">JezPress</div>
            <div className="font-mono">{status.version}</div>
          </div>
          <div className="p-2 rounded bg-muted/50">
            <div className="text-muted-foreground text-xs">WordPress</div>
            <div className="font-mono">{status.wp_version}</div>
          </div>
          <div className="p-2 rounded bg-muted/50">
            <div className="text-muted-foreground text-xs">PHP</div>
            <div className="font-mono">{status.php_version}</div>
          </div>
        </div>
      )}

      {/* Settings Controls */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Settings
        </h4>

        {/* OAuth Only Toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg border">
          <div className="space-y-0.5">
            <Label htmlFor="oauth-only-manage" className="font-medium">OAuth Only</Label>
            <div className="text-sm text-muted-foreground">
              Hide password login, require Google sign-in
            </div>
          </div>
          <Switch
            id="oauth-only-manage"
            checked={remoteSettings?.settings?.disable_password_login ?? false}
            onCheckedChange={handleOAuthToggle}
            disabled={settingsLoading || updateSettings.isPending}
          />
        </div>
      </div>

      {/* Plugins */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Plug className="h-4 w-4" />
            Plugins
            {pluginsData && (
              <Badge variant="secondary" className="ml-1">
                {pluginsData.total}
              </Badge>
            )}
          </h4>
          {pluginsData && pluginsData.updates_available > 0 && (
            <Badge variant="default">
              {pluginsData.updates_available} update{pluginsData.updates_available !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {pluginsLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : pluginsData?.plugins ? (
          <div className="space-y-2">
            {pluginsData.plugins.map((plugin) => (
              <div
                key={plugin.slug}
                className="flex items-center justify-between p-3 rounded-lg border text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{plugin.name}</div>
                  <div className="text-muted-foreground text-xs flex items-center gap-2">
                    <span className="font-mono">v{plugin.version}</span>
                    {!plugin.active && (
                      <Badge variant="outline" className="text-xs">Inactive</Badge>
                    )}
                  </div>
                </div>
                {plugin.update_available && plugin.new_version && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdatePlugin(plugin.slug)}
                    disabled={updatePlugin.isPending && updatePlugin.variables?.slug === plugin.slug}
                    className="ml-2 shrink-0"
                  >
                    {updatePlugin.isPending && updatePlugin.variables?.slug === plugin.slug ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <ArrowUpCircle className="h-4 w-4 mr-1" />
                    )}
                    {plugin.new_version}
                  </Button>
                )}
                {!plugin.update_available && (
                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0 ml-2" />
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to load plugins</p>
        )}
      </div>
    </div>
  )
}
